import React, { useEffect, useState, useMemo } from "react";

import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";

import classes from "./OwnerOccupancy.module.scss";
import { useDispatch, useSelector } from "react-redux";
import { IYearlyOccupancy } from "../../../../interfaces";
import { yearlyOccupancyGeneralSelector } from "../../../../../redux/selectors/occupancyRateSelector";
import { isMobileModeSelector } from "../../../../../redux/selectors/appStatusSelector";
import { OCCUPANCY_BY_YEAR_REQUEST } from "../../../../../redux/actions";
import { performanceDataFilterSelector } from "../../../../../redux/selectors/performanceDataSelector";

const tableConfig = [
  {
    label: "Year",
    id: "year",
  },
  {
    label: "January",
    id: "january",
  },
  {
    label: "February",
    id: "february",
  },
  {
    label: "March",
    id: "match",
  },
  {
    label: "April",
    id: "april",
  },
  {
    label: "May",
    id: "may",
  },
  {
    label: "June",
    id: "june",
  },
  {
    label: "July",
    id: "july",
  },
  {
    label: "August",
    id: "august",
  },
  {
    label: "September",
    id: "september",
  },
  {
    label: "October",
    id: "october",
  },
  {
    label: "November",
    id: "november",
  },
  {
    label: "December",
    id: "december",
  },
];

interface ITablePos {
  x: number;
  y: number;
}

export const OwnerOccupancy = () => {
  const generalDataFromStore = useSelector(yearlyOccupancyGeneralSelector);
  const isMobileView = useSelector(isMobileModeSelector);
  const filters = useSelector(performanceDataFilterSelector);
  const dispatch = useDispatch();
  
  const [generalData, setGeneralData] = useState<any>({});  // Local state for general data
  const [hoveredMatrix, setHoveredMatrix] = useState<undefined | ITablePos>();
  
  // Update local state with general data from the store
  useEffect(() => {
    if (generalDataFromStore) {
      setGeneralData(generalDataFromStore);  // Update generalData state when store changes
    }
  }, [generalDataFromStore]);
  
  // Filter and aggregate data based on filters
  const filteredData = useMemo(() => {
    if (!generalData || !Object.keys(generalData).length) return {};  // Return empty object if no generalData
  
    return Object.keys(generalData).reduce((result: any, year: string) => {
      const yearData = generalData[year];
      if (!yearData || yearData.length === 0) return result;
  
      // Separate data for is_listed == 1 (0-12) and is_listed == 0 (12-24)
      const notListedData = yearData.slice(0, 12);
      let listedData = yearData.slice(12, 24);
      listedData = listedData.length > 0 ? listedData : notListedData;
  
      // Apply filters
      const filterYes = filters?.listed?.yes ?? true;
      const filterNo = filters?.listed?.no;
      const filterBeds = filters?.ownersToRequest?.length > 0 ? filters.ownersToRequest.length : false;
  
      const monthlyData = listedData.map((monthData: IYearlyOccupancy, index: number) => {
        let combinedData = null;
  
        if (filterYes && filterNo && !filterBeds) {
          // Combine both listed and not-listed data
          combinedData = {
            ...monthData,
            occupancy_rate_portal: (monthData.occupancy_rate_portal || 0) +
              (notListedData[index]?.occupancy_rate_portal || 0),
            occupancy_rate_guestyical: (monthData.occupancy_rate_guestyical || 0) +
              (notListedData[index]?.occupancy_rate_guestyical || 0),
            occupancy_rate_ical: (monthData.occupancy_rate_ical || 0) +
              (notListedData[index]?.occupancy_rate_ical || 0),
          };
        } else if (filterYes) {
          // Only listed data
          combinedData = monthData;
        } else if (filterNo) {
          // Only not-listed data
          combinedData = notListedData[index] || null;
        }
        return combinedData;
      });
  
      result[year] = monthlyData.filter(Boolean);  // Remove nulls
      return result;
    }, {});
  }, [generalData, filters]);
  
  useEffect(() => {
    // Fetch data for the required years (if no data)
    dispatch({
      type: OCCUPANCY_BY_YEAR_REQUEST,
      payload: {
        owners: filters?.ownersToRequest || [],
        properties: filters?.propertiesToRequest || [],
        beds: filters?.bedroomsToRequest || [],
        listed: filters?.listedToRequest || [],
        year: ["2019", "2020", "2021", "2022", "2023", "2024", "2025"],
      },
    });
  }, [dispatch, filters]);  

  useEffect(() => {
    if (!generalData) return;
    const validYears = Object.keys(generalData).filter((year) =>
      filters?.year?.includes(year)
    );
  
    // Ensure generalData only contains years returned by the API
    Object.keys(generalData).forEach((year) => {
      if (!validYears.includes(year)) {
        delete generalData[year];
      }
    });
    setGeneralData(validYears);
  }, [filters]);   


  const handleHover = (rowIndex?: number, cellIndex?: number) => {
    if (Number.isInteger(rowIndex) && Number.isInteger(cellIndex)) {
      setHoveredMatrix({ x: rowIndex ?? 0, y: cellIndex ?? 0 });
    } else {
      setHoveredMatrix(undefined);
    }
  };

  return (
    <Box className={classes.ownerOccupancyWrapper} id="ownerOccupancy">
      <h4 style={{ fontSize: isMobileView ? "16px" : "24px" }}>
        Owner Occupancy Rate
      </h4>
      <Box
        style={{
          overflowX: "auto",
          marginTop: "8px",
          maxHeight: isMobileView ? undefined : "240px",
          overflowY: isMobileView ? undefined : "scroll",
        }}
      >
        <Table sx={{ mt: 3 }} className={classes.table}>
          <TableHead>
            <TableRow>
              {!isMobileView ? (
                tableConfig.map((column, cellIndex: number) => (
                  <TableCell
                    className={classes.headerCell}
                    key={column.id}
                    sx={{
                      backgroundColor:
                        hoveredMatrix && hoveredMatrix.y === cellIndex - 1
                          ? "#EEFCF8"
                          : undefined,
                    }}
                  >
                    {column.label}
                  </TableCell>
                ))
              ) : (
                <>
                  <TableCell className={classes.headerCell}></TableCell>
                  {filteredData &&
                    Object.keys(filteredData)
                      .reverse()
                      .map((year: string, cellIndex: number) => (
                        <TableCell
                          className={classes.headerCell}
                          sx={{
                            backgroundColor:
                              hoveredMatrix && hoveredMatrix.y === cellIndex
                                ? "#EEFCF8"
                                : undefined,
                          }}
                          key={year}
                        >
                          {year}
                        </TableCell>
                      ))}
                </>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredData &&
              (!isMobileView
                ? Object.keys(filteredData)
                    .reverse()
                    .map((year: string, rowIndex: number) => (
                      <TableRow
                        key={year}
                        style={{
                          backgroundColor:
                            hoveredMatrix && hoveredMatrix.x === rowIndex
                              ? "#EEFCF8"
                              : undefined,
                        }}
                      >
                        <TableCell className={classes.yearCell}>
                          {year}
                        </TableCell>
                        {filteredData?.[year].map(
                          (monthData: IYearlyOccupancy, cellIndex: number) => (
                            <TableCell
                              sx={{
                                padding: "4px",
                                backgroundColor:
                                  hoveredMatrix && hoveredMatrix.y === cellIndex
                                    ? "#EEFCF8"
                                    : undefined,
                              }}
                              onMouseEnter={() =>
                                handleHover(rowIndex, cellIndex)
                              }
                              onMouseLeave={() => handleHover()}
                              key={monthData.start_date}
                            >
                              <Box
                                className={classes.monthCell}
                                sx={{
                                  backgroundColor:
                                    hoveredMatrix &&
                                    (hoveredMatrix.x === rowIndex ||
                                      hoveredMatrix.y === cellIndex)
                                      ? `rgba(186, 242, 228, ${(
                                        (monthData?.occupancy_rate_portal || 0) +
                                        (monthData?.occupancy_rate_guestyical || 0) +
                                        (monthData?.occupancy_rate_ical || 0)
                                      ).toFixed(2)})`
                                    : `rgba(140, 144, 172, ${(
                                        (monthData?.occupancy_rate_portal || 0) +
                                        (monthData?.occupancy_rate_guestyical || 0) +
                                        (monthData?.occupancy_rate_ical || 0)
                                      ).toFixed(2)})`,
                                  border:
                                    hoveredMatrix &&
                                    hoveredMatrix.x === rowIndex &&
                                    hoveredMatrix.y === cellIndex
                                      ? "1px solid #00CCA0"
                                      : "inherit",
                                  color:
                                    hoveredMatrix &&
                                    hoveredMatrix.x === rowIndex &&
                                    hoveredMatrix.y === cellIndex
                                      ? "#02094F !important"
                                      : "inherit",
                                  fontWeight:
                                    hoveredMatrix &&
                                    hoveredMatrix.x === rowIndex &&
                                    hoveredMatrix.y === cellIndex
                                      ? "500 !important"
                                      : "inherit",
                                }}
                              >
                                {(
                                  ((monthData?.occupancy_rate_portal || 0) +
                                  (monthData?.occupancy_rate_guestyical || 0) +
                                  (monthData?.occupancy_rate_ical || 0)) *
                                  100
                                ).toFixed(2)}
                                %
                              </Box>
                            </TableCell>
                          )
                        )}
                      </TableRow>
                    ))
                : tableConfig.slice(1).map((row, index) => (
                    <TableRow
                      key={row.id}
                      style={{
                        backgroundColor:
                          hoveredMatrix && hoveredMatrix.x === index
                            ? "#EEFCF8"
                            : undefined,
                      }}
                    >
                      <TableCell className={classes.yearCell}>
                        {row.label}
                      </TableCell>
                      {Object.keys(filteredData)
                        .reverse()
                        .map((year: string, cellIndex: number) => (
                          <TableCell
                            sx={{
                              padding: "4px",
                              backgroundColor:
                                hoveredMatrix && hoveredMatrix.y === cellIndex
                                  ? "#EEFCF8"
                                  : undefined,
                            }}
                            key={year}
                          >
                            <Box
                              className={classes.monthCell}
                              sx={{
                                backgroundColor:
                                  hoveredMatrix &&
                                  (hoveredMatrix.x === index ||
                                    hoveredMatrix.y === cellIndex)
                                    ? `rgba(186, 242, 228, ${(
                                      (filteredData?.[year]?.[index]
                                        ?.occupancy_rate_portal || 0) +
                                      (filteredData?.[year]?.[index]
                                        ?.occupancy_rate_guestyical || 0) +
                                      (filteredData?.[year]?.[index]
                                        ?.occupancy_rate_ical || 0)
                                    ).toFixed(2)})`
                                  : `rgba(140, 144, 172, ${(
                                      (filteredData?.[year]?.[index]
                                        ?.occupancy_rate_portal || 0) +
                                      (filteredData?.[year]?.[index]
                                        ?.occupancy_rate_guestyical || 0) +
                                      (filteredData?.[year]?.[index]
                                        ?.occupancy_rate_ical || 0)
                                    ).toFixed(2)})`,
                                border:
                                  hoveredMatrix &&
                                  hoveredMatrix.x === index &&
                                  hoveredMatrix.y === cellIndex
                                    ? "1px solid #00CCA0"
                                    : "inherit",
                                color:
                                  hoveredMatrix &&
                                  hoveredMatrix.x === index &&
                                  hoveredMatrix.y === cellIndex
                                    ? "#02094F !important"
                                    : "inherit",
                                fontWeight:
                                  hoveredMatrix &&
                                  hoveredMatrix.x === index &&
                                  hoveredMatrix.y === cellIndex
                                    ? "500 !important"
                                    : "inherit",
                              }}
                              onMouseEnter={() => handleHover(index, cellIndex)}
                              onMouseLeave={() => handleHover()}
                            >
                              {(
                                ((filteredData?.[year]?.[index]
                                  ?.occupancy_rate_portal || 0) +
                                  (filteredData?.[year]?.[index]
                                    ?.occupancy_rate_guestyical || 0) +
                                  (filteredData?.[year]?.[index]
                                    ?.occupancy_rate_ical || 0)) *
                                100
                              ).toFixed(2)}
                              %
                            </Box>
                          </TableCell>
                        ))}
                    </TableRow>
                  )))}
          </TableBody>
        </Table>
      </Box>
    </Box>
  );
};
